import { api } from '../../services/api';
import type { RoomResponse } from '../../types/auth';
import { isSessionHost } from '../../utils/roomHostsPolicy';
import { LIVE_SUBSCRIBERS_ONLY_CODE } from '../live/liveDetailHelpers';

/** Чья подписка открывает вход в сессию. */
export interface SubscribersGateTarget {
  masterProfileId: number;
  masterName: string | null;
}

/**
 * CORE-1712 — сессия «только подписчикам» и человек «с улицы».
 *
 * Сервер пока пускает в такую комнату любого с инвайт-кодом: `/join` и хаб не
 * смотрят на подписку (QA 17.10, комната 6038). Продукт: не подписчик видит
 * карточку, а при входе — «сначала подпишитесь». Поэтому до входа приложение
 * спрашивает подписку само, а отказ сервера с кодом CORE-980 — тот же, что у
 * старых лайвов, — понимает заранее, к его релизу.
 *
 * Проверяем только вошедшего в аккаунт: гостю подписаться нечем. Ведущих и
 * соведущих не проверяем — это их сессия.
 */
export function subscribersGateTarget(
  room: RoomResponse | null | undefined,
  userId: string | null | undefined,
): SubscribersGateTarget | null {
  if (!room || !userId) return null;
  if (room.visibility !== 'Subscribers') return null;
  if (isSessionHost(room, userId)) return null;
  if (room.hostMasterProfileId == null) return null;
  return { masterProfileId: room.hostMasterProfileId, masterName: room.hostName || null };
}

/**
 * Заперт ли вход. Узнать подписку не удалось — не запираем: решает сервер, а
 * замок из-за чужого сбоя хуже пропущенного не подписчика.
 */
export async function isLockedForSubscribers(target: SubscribersGateTarget): Promise<boolean> {
  try {
    const state = await api.getMasterSubscriptionState(target.masterProfileId);
    return state.isSubscribed !== true;
  } catch {
    return false;
  }
}

/** Подписка с экрана замка; `false` — не получилось. */
export async function subscribeToGateMaster(target: SubscribersGateTarget): Promise<boolean> {
  try {
    const state = await api.subscribeToMaster(target.masterProfileId);
    return state?.isSubscribed !== false;
  } catch {
    return false;
  }
}

/** Отказ сервера «только подписчикам»: 403 с кодом, а не голый статус. */
export function isSubscribersOnlyError(err: unknown): boolean {
  const e = err as { code?: unknown; Code?: unknown; status?: unknown } | null | undefined;
  return !!e && e.status === 403 && (e.code ?? e.Code) === LIVE_SUBSCRIBERS_ONLY_CODE;
}

/**
 * CORE-1881 — отказ токена звонка «только подписчикам» окончателен: сообщаем о
 * нём экрану и пробрасываем дальше. Без этого веб повторял вход впустую (прод
 * 26.10 и 28.10, комнаты 642 и 710), а человек видел комнату без звонка.
 */
export function withSubscribersDenial<A extends unknown[], T>(
  fetchToken: (...args: A) => Promise<T>,
  onDenied: (err: unknown) => void,
): (...args: A) => Promise<T> {
  return (...args) => fetchToken(...args).catch((err: unknown) => {
    if (isSubscribersOnlyError(err)) onDenied(err);
    throw err;
  });
}

/**
 * CORE-2053 — сервер отказал в напоминании «только подписчикам» (тот же 403
 * `live_subscribers_only`, что у входа): на кого предложить подписаться.
 * Мастер-профиль берём из ответа, нет его — из комнаты; `null` — отказ не этот
 * или подписаться не на кого.
 */
export function subscribersOnlyDenialMasterId(
  err: unknown,
  room: Pick<RoomResponse, 'hostMasterProfileId'> | null | undefined,
): number | null {
  if (!isSubscribersOnlyError(err)) return null;
  const e = err as { hostMasterProfileId?: unknown; HostMasterProfileId?: unknown };
  const fromBody = e.hostMasterProfileId ?? e.HostMasterProfileId;
  if (typeof fromBody === 'number') return fromBody;
  return room?.hostMasterProfileId ?? null;
}
